import { useState, useEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import styles from './Navbar.module.css'

// ── ICONS ─────────────────────────────────────────────────────────────────────
const BotIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v4"/>
    <circle cx="8"  cy="16" r="1.2" fill="currentColor" stroke="none"/>
    <circle cx="16" cy="16" r="1.2" fill="currentColor" stroke="none"/>
    <line x1="9" y1="20" x2="15" y2="20" strokeWidth="1.5"/>
  </svg>
)

const ZapIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
)

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)

const NAV_LINKS = [
  { to: '/',        label: 'Deploy',  end: true },
  { to: '/servers', label: 'Servers', end: false },
  { to: '/contact', label: 'Contact', end: false },
  { to: '/privacy', label: 'Privacy', end: false },
]

export default function Navbar({ totalBots, onlineCount, serverCount }) {
  const [scrolled,    setScrolled]    = useState(false)
  const [mobileOpen,  setMobileOpen]  = useState(false)
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0, opacity: 0 })
  const navRef   = useRef(null)
  const linksRef = useRef([])
  const location = useLocation()

  // scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // close mobile on route change
  useEffect(() => { setMobileOpen(false) }, [location])

  // sliding active indicator
  useEffect(() => {
    const idx = NAV_LINKS.findIndex(l =>
      l.end ? location.pathname === l.to : location.pathname.startsWith(l.to)
    )
    const el = linksRef.current[idx]
    if (!el) { setIndicatorStyle(s => ({ ...s, opacity: 0 })); return }
    const rect = el.getBoundingClientRect()
    const navRect = el.closest('nav')?.getBoundingClientRect() || { left: 0 }
    setIndicatorStyle({
      width: rect.width,
      left:  rect.left - navRect.left,
      opacity: 1,
    })
  }, [location.pathname])

  const isOnline = onlineCount > 0

  return (
    <>
      <nav className={`${styles.nav} ${scrolled ? styles.scrolled : ''}`}>
        {/* PROGRESS LINE */}
        <div className={styles.progressLine} />

        <div className={styles.inner}>
          {/* LOGO */}
          <NavLink to="/" className={styles.logo}>
            <div className={styles.logoMark}>
              <BotIcon />
              <div className={styles.logoMarkGlow} />
            </div>
            <div className={styles.logoText}>
              <span className={styles.logoName}>SAVIYA</span>
              <span className={styles.logoDash}>-X</span>
            </div>
            <div className={styles.logoBeta}>BETA</div>
          </NavLink>

          {/* DESKTOP LINKS */}
          <div className={styles.links} role="navigation">
            {/* sliding underline indicator */}
            <div
              className={styles.indicator}
              style={{
                width:   indicatorStyle.width,
                transform: `translateX(${indicatorStyle.left}px)`,
                opacity: indicatorStyle.opacity,
              }}
            />
            {NAV_LINKS.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                ref={el => linksRef.current[i] = el}
                className={({ isActive }) =>
                  `${styles.link} ${isActive ? styles.linkActive : ''}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* RIGHT: STATUS + MOBILE TOGGLE */}
          <div className={styles.right}>
            <div className={styles.statusChip}>
              <span className={`${styles.statusDot} ${isOnline ? styles.dotOnline : styles.dotOffline}`} />
              <span className={styles.statusLabel}>
                <span className={styles.statusBots}>{totalBots}</span>
                <span className={styles.statusSep}> bots</span>
              </span>
              <div className={styles.statusDivider} />
              <ZapIcon />
              <span className={styles.statusSep}>{onlineCount}/{serverCount}</span>
            </div>

            <button
              className={styles.mobileToggle}
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE DRAWER */}
      <div className={`${styles.drawer} ${mobileOpen ? styles.drawerOpen : ''}`}>
        <div className={styles.drawerInner}>
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `${styles.drawerLink} ${isActive ? styles.drawerLinkActive : ''}`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <div className={styles.drawerStatus}>
            <span className={`${styles.statusDot} ${isOnline ? styles.dotOnline : styles.dotOffline}`} />
            {totalBots} active bots · {onlineCount}/{serverCount} servers up
          </div>
        </div>
      </div>

      {/* MOBILE BACKDROP */}
      {mobileOpen && (
        <div className={styles.backdrop} onClick={() => setMobileOpen(false)} />
      )}
    </>
  )
}
